import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchMatchProperties,
  createMatchProperty,
  updateMatchProperty,
  deleteMatchProperty,
} from "@/store/matchPropertiesSlice";
import { useNotification } from "@/context/NotificationContext";
import { useApiErrorMessage } from "@/hooks/useApiErrorMessage";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { SearchField } from "@/components/SearchField";
import type { MatchProperty } from "@/types";

interface PropertyForm {
  name: string;
  location: "header" | "query" | "body";
  shortName: string;
  displayName: string;
  slug: string;
  description: string;
}

const locationColors: Record<string, "primary" | "secondary" | "warning"> = {
  header: "primary",
  query: "secondary",
  body: "warning",
};

export function Properties() {
  const { t } = useTranslation();
  const getErrorMessage = useApiErrorMessage();
  const dispatch = useAppDispatch();
  const { items, loading } = useAppSelector((s) => s.matchProperties);
  const { notify } = useNotification();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MatchProperty | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MatchProperty | null>(null);

  const { control, handleSubmit, reset } = useForm<PropertyForm>({
    defaultValues: {
      name: "",
      location: "header",
      shortName: "",
      displayName: "",
      slug: "",
      description: "",
    },
  });

  useEffect(() => {
    dispatch(fetchMatchProperties());
  }, [dispatch]);

  const filtered = items.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase()),
  );
  const inUse = items.filter(
    (m) => (m.featureFlagMatchProperties?.length || 0) > 0,
  ).length;

  const openCreate = () => {
    setEditing(null);
    reset({
      name: "",
      location: "header",
      shortName: "",
      displayName: "",
      slug: "",
      description: "",
    });
    setDialogOpen(true);
  };
  const openEdit = (mp: MatchProperty) => {
    setEditing(mp);
    reset({
      name: mp.name,
      location: mp.location,
      shortName: mp.shortName || "",
      displayName: mp.displayName || "",
      slug: mp.slug || "",
      description: mp.description || "",
    });
    setDialogOpen(true);
  };

  const onSubmit = async (data: PropertyForm) => {
    try {
      if (editing) {
        await dispatch(updateMatchProperty({ id: editing.id, data })).unwrap();
        notify(t("properties.updated"), "success");
      } else {
        await dispatch(createMatchProperty(data)).unwrap();
        notify(t("properties.created"), "success");
      }
      setDialogOpen(false);
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteMatchProperty(deleteTarget.id)).unwrap();
      notify(t("properties.deleted"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
    setDeleteTarget(null);
  };

  return (
    <Box>
      <PageHeader
        title={t("properties.title")}
        subtitle={t("properties.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            color="secondary"
          >
            {t("properties.create")}
          </Button>
        }
      />
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("properties.totalProperties")}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                }}
              >
                {items.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("properties.inUse")}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                }}
              >
                {inUse}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={t("common.search")}
      />
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("common.loading")}
              </Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {search ? t("common.noResults") : t("properties.noProperties")}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filtered.map((mp, idx) => (
                <ListItem
                  key={mp.id}
                  divider={idx < filtered.length - 1}
                  sx={{ px: 3, py: 2 }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                          }}
                        >
                          {mp.name}
                        </Typography>
                        <Chip
                          label={mp.location.toUpperCase()}
                          size="small"
                          color={locationColors[mp.location] || "default"}
                        />
                        {mp.featureFlagMatchProperties &&
                          mp.featureFlagMatchProperties.length > 0 && (
                            <Chip
                              label={t("properties.flagsLinkedCount", {
                                count: mp.featureFlagMatchProperties.length,
                              })}
                              size="small"
                              variant="outlined"
                            />
                          )}
                      </Box>
                    }
                    secondary={mp.description || mp.slug}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => openEdit(mp)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(mp)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editing ? t("properties.edit") : t("properties.create")}
          </DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: "8px !important",
            }}
          >
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("properties.name")}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name="location"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControl fullWidth required>
                  <InputLabel>{t("properties.location")}</InputLabel>
                  <Select {...field} label={t("properties.location")}>
                    <MenuItem value="header">
                      {t("properties.locationHeader")}
                    </MenuItem>
                    <MenuItem value="query">
                      {t("properties.locationQuery")}
                    </MenuItem>
                    <MenuItem value="body">
                      {t("properties.locationBody")}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="shortName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("properties.shortName")}
                  fullWidth
                />
              )}
            />
            <Controller
              name="slug"
              control={control}
              render={({ field }) => (
                <TextField {...field} label={t("properties.slug")} fullWidth />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("properties.description")}
                  multiline
                  rows={2}
                  fullWidth
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="contained">
              {editing ? t("common.save") : t("common.create")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t("properties.delete")}
        message={t("properties.deleteConfirm")}
        onConfirm={onDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
